import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useVisibleProducts } from '@/hooks/useVisibleProducts'
import ProductIcon from '@/components/ProductIcon'

interface Card {
  id: string
  name: string
  subtitle: string
  description: string
  features: string[]
  href: string
  iconPath: string
  external: boolean
  accent: 'blue' | 'green'
}

export default function ProductsOverviewCard() {
  const { t, i18n } = useTranslation('home')
  const products = useVisibleProducts()

  // A built-in product that still has its shipped description takes its
  // card texts from the locale files; anything else shows what the owner typed.
  const cards: Card[] = products.map((p) => {
    const key = p.key ? `products.${p.id.replace(/-/g, '')}` : ''
    const shipped = key && t(`${key}.name`, { defaultValue: '' })
    const features = shipped ? (t(`${key}.features`, { returnObjects: true, defaultValue: [] }) as string[]) : []
    return {
      id: p.id,
      name: p.name,
      subtitle: shipped ? t(`${key}.subtitle`, { defaultValue: '' }) : '',
      description: shipped ? t(`${key}.description`, { defaultValue: p.description }) : p.description,
      features: Array.isArray(features) ? features : [],
      href: p.href(i18n.language),
      iconPath: p.iconPath,
      external: p.external,
      accent: p.id === 'pipeline-studio' ? 'blue' : 'green'
    }
  })

  const body = (card: Card) => (
    <div className="bg-linear-to-br from-green-50 via-emerald-50 to-green-100 rounded-xl p-6 border border-white/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-0.5 hover:scale-[1.01]">
      <div className="flex items-center mb-4">
        <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4">
          <ProductIcon name={card.name} src={card.iconPath} className="w-12 h-12 rounded-xl" />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-800">{card.name}</h3>
          {card.subtitle && <p className="text-gray-600 text-sm">{card.subtitle}</p>}
        </div>
      </div>
      {card.description && <p className="text-gray-700 text-sm mb-4 leading-relaxed">{card.description}</p>}
      {card.features.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {card.features.map((feature, index) => (
            <span
              key={index}
              className={`px-3 py-1 rounded-full text-xs font-medium ${
                card.accent === 'blue' ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-green-50 text-green-700 border border-green-200'
              }`}
            >
              {feature}
            </span>
          ))}
        </div>
      )}
    </div>
  )

  return (
    <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl p-8 border border-gray-200/50 mb-8 transition-all duration-300">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-text-dark">{t('products.title')}</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {cards.map((card) =>
          card.external ? (
            <a key={card.id} href={card.href} className="group block" target="_blank" rel="noopener noreferrer">
              {body(card)}
            </a>
          ) : (
            <Link key={card.id} to={card.href} className="group block">
              {body(card)}
            </Link>
          )
        )}
      </div>
    </div>
  )
}
